/**
 * The unified event record: everything that happens, in one shape.
 *
 * Systems emit; witnesses (and later, belief) read. An event is a place, a
 * time, a footprint and a cause. Emission order is exactly system order, so
 * an identical run mints identical ids. One subscriber may watch the log
 * grow (onEmit) — it runs synchronously after the record is stored, so
 * witnessing is part of the same deterministic history it observes. This is
 * the witness system before belief exists: gods will be inferred from this
 * log, not observed.
 */

import type { EventBusSnapshot } from '@/sim/persistence/snapshot';

export type EventType =
  | 'fire_started' | 'fire_burned'
  | 'band_arrived' | 'band_departed' | 'band_migrated'
  /** A community came apart into two that will not be the same for long. */
  | 'band_split'
  /** Two communities that kept meeting became one. */
  | 'band_merged'
  /** A branch of a people has been alone long enough to be its own. */
  | 'culture_diverged'
  | 'wildlife_migrated' | 'predator_incident'
  /**
   * A species fell under one head in a cell that could still carry a herd of
   * them; and one came back — two head after at least half a year away
   * (Design III §50's "the wolves returned after ninety-three years"). Both
   * natural, both from `sim/wildlife/populations.ts` (Lane B, 2026-08-15):
   * data `{ species, head }` and `{ species, head, absentDays }`; nothing in
   * either names a hand. Rare on the shipped century — 0 vanishings and ~4
   * returns a year over two god-free years — because extirpation is rare in
   * this world; they fire from big burns' browsers and a Weaken's valley.
   */
  | 'wildlife_vanished' | 'wildlife_returned'
  | 'resource_shortage'
  /**
   * A named person began, and a named person ended (Design III §139).
   *
   * These are new, and their absence was measurable: `tools/hour.ts` counted
   * 0.368 births and 0.355 deaths per canonical hour — fourteen times every
   * construction event on the island put together — and had to find them by
   * DIFFING THE ROSTER, because the world had no word for either. A death was
   * emitted as `resource_shortage` carrying `resource: 'life'`, which the
   * Chronicle rendered as *"hunger took hold"*: the most legible thing that
   * happens to a person read as a famine warning, and famine had nothing to do
   * with it. A birth was emitted as nothing at all.
   *
   * Mortality (`sim/mortals/mortality.ts`) has been built since 2026-08-12 and
   * is the keystone Design III's whole attachment pillar rests on. A world
   * where people die and no one can say so is not that pillar.
   */
  | 'person_born' | 'person_died'
  /**
   * A named person stopped being who they were, in the world's own words
   * (`PLAYABLE.md` P2: *has become bolder over time*; P3: horizon C for a
   * Strengthen laid on a life).
   *
   * An axis crossed the line the card speaks at — `STANDS_OUT` up, or
   * `STANDS_APART` down (`sim/mortals/people.ts`) — so what a neighbour would
   * call this person changed: *curious* appeared, or *wary* did. Emitted by
   * the band for any cause, because a life's own work crosses these lines far
   * more often than a god does (law 4, kept honest): cause is always `mortal`,
   * salience is under `SALIENT` so it is nobody's news and nobody's memory,
   * and the trace it leaves is on the person (`PersonState.became`). When a
   * cast carried the axis over, the event descends from the cast through
   * `parentEventIds` — which `perceive()` strips — and the Chronicle lets the
   * strand endure by it. That is the only road a god's press has to history,
   * and it is a trace rather than a buff: the person is different in a way
   * the world can say, and nothing was added to them that they did not do.
   *
   * Data `{ who, id, axis, up, targetId }`; `targetId` is the person's id, so
   * the Chronicle credits only the strand laid on THIS person.
   */
  | 'person_changed'
  /**
   * A thing in the world stopped being ordinary (law 4, Design V §16).
   *
   * `world/things.ts` has always computed the crossing and thrown it away, so
   * the single mechanic that store exists to express — repetition promotes —
   * happened in total silence. Cause is `divine` and therefore reaches every
   * eye as *unexplained*: a witness knows a particular tree has become
   * strange, and nothing whatever about a hand.
   */
  | 'thing_promoted'
  | 'structure_started' | 'structure_completed'
  /**
   * A band took over a standing structure nobody owned (2026-08-23). The
   * inverse of `camp_abandoned`, and it exists because the shipped century
   * had none of it: all 42 unowned structures on the island had rotted below
   * functional — every abandoned camp decayed to ruin with living neighbours
   * standing beside it, because need could only ever open a NEW work site.
   * Claiming is need-driven (it substitutes for building, one per day at
   * most), so surplus ruins still fall; only what somebody would otherwise
   * have rebuilt is kept standing. Data: `{ structure }`; cause is the
   * claiming band. Law 10 holds: nothing here names a hand, because no hand
   * is involved — this is mortals mending their own ground.
   */
  | 'structure_claimed'
  | 'path_formed' | 'camp_abandoned'
  /** Somebody went to look at where a sign seemed to point, and found out. */
  | 'omen_answered'
  /** A god came down and bound itself to a place. */
  | 'anchor_placed'
  /** Mortals came across one, and now know the place is there. */
  | 'anchor_found'
  /** Repeated visits turned the place into something more than ground. */
  | 'sacred_raised'
  | 'divine_strike' | 'divine_rain' | 'divine_strengthen' | 'divine_weaken'
  /** A god physically picked something up and put it down (Design IV §21). */
  | 'divine_grasp'
  /**
   * A god left part of itself burning at one of its stones (law 31). Emitted
   * once, at the kindling — the daily work that follows is deliberately
   * silent, so an aura reaches mortals as a place that is simply good rather
   * than as a miracle anybody saw.
   */
  | 'divine_aura'
  /** And the day a god ran too poor to keep holding it. */
  | 'divine_aura_spent'
  /** A god took hold of the ground itself and moved it (`world/earth.ts`). */
  | 'divine_earth'
  /** The informational verb: it changes attention, never conditions. */
  | 'divine_omen';

/**
 * Who did it — as far as the simulation knows. Witnesses may conclude
 * otherwise; that gap between cause and belief is the whole game.
 *
 * `godId` on a divine cause is the world's own bookkeeping and nothing
 * else's. It is how the server will one day settle which deity did what,
 * and it must never reach a mortal, a band's memory, or a rival god: see
 * perceivedCause below, and the wall enforced by test/deity.ts.
 */
export type EventCause =
  | { kind: 'natural' }
  | { kind: 'mortal'; bandId?: string }
  | { kind: 'divine'; godId?: string };

/**
 * What anyone in the world can actually tell about why a thing happened.
 *
 * Weather is weather and people are people — those are legible. A divine
 * act is not: it arrives as an event with no author, and *unexplained* is
 * the whole seed of religion. Mortals will invent a cause for it; rival
 * gods will guess at a hand; both may be wrong, and the world will never
 * correct them. TRUE CAUSE ≠ PERCEIVED CAUSE, expressed as one function.
 */
export type PerceivedCause = 'natural' | 'mortal' | 'unexplained';

export const perceivedCause = (e: EventRecord): PerceivedCause =>
  e.cause.kind === 'divine' ? 'unexplained' : e.cause.kind;

/**
 * The event as an observer receives it: everything about *what happened*,
 * nothing about who is responsible. This is the only shape that should ever
 * cross from the world's ledger into a mind — mortal or divine.
 */
export interface PerceivedEvent {
  id: string;
  type: EventType;
  time: number;
  x: number;
  z: number;
  radius: number;
  magnitude: number;
  salience: number;
  cause: PerceivedCause;
  data?: Record<string, number | string | boolean>;
}

export const perceive = (e: EventRecord): PerceivedEvent => ({
  id: e.id, type: e.type, time: e.time, x: e.x, z: e.z,
  radius: e.radius, magnitude: e.magnitude, salience: e.salience,
  cause: perceivedCause(e),
  ...(e.data ? { data: { ...e.data } } : {}),
});

export interface EventRecord {
  id: string;
  type: EventType;
  /** World-seconds. */
  time: number;
  x: number;
  z: number;
  /** Footprint in metres — how far the thing itself reached. */
  radius: number;
  /** How big it was, in the emitter's own units (area burned, head count…). */
  magnitude: number;
  /** How much it demands explanation, 0..1. Salience is what breeds gods. */
  salience: number;
  cause: EventCause;
  /**
   * Events this one grew out of — the strike behind the fire. The simulation
   * knows what caused what; mortals only ever guess.
   */
  parentEventIds?: string[];
  data?: Record<string, number | string | boolean>;
}

/** Retention cap: old events fall off silently. Memory is for the belief system. */
const MAX_EVENTS = 2000;

export class EventBus {
  private readonly events: EventRecord[] = [];
  private seq = 0;
  private listener?: (e: EventRecord) => void;

  /**
   * Watch the log grow. One subscriber only — the simulation's witness
   * wiring — called synchronously inside emit, after the record is stored.
   * Deterministic by construction: it runs at emission time, which is
   * simulation time, in emission order.
   */
  onEmit(cb: (e: EventRecord) => void): void {
    this.listener = cb;
  }

  /**
   * Record an event. The id is minted purely from emission order, so a
   * deterministic run assigns deterministic ids — safe to persist and to
   * reference from belief state later.
   */
  emit(e: Omit<EventRecord, 'id'>): EventRecord {
    // spread first: even a stray runtime `id` on the input cannot shadow ours
    const rec: EventRecord = { ...e, id: `${e.type}@${e.time}#${this.seq++}` };
    this.events.push(rec);
    if (this.events.length > MAX_EVENTS) {
      this.events.splice(0, this.events.length - MAX_EVENTS);
    }
    this.listener?.(rec);
    return rec;
  }

  /**
   * Events since `time >= since` whose footprint touches the query disc —
   * a great fire is witnessable from beyond its own edge, so the two radii
   * add. This is the only spatial query a witness gets.
   */
  near(x: number, z: number, radius: number, since: number): EventRecord[] {
    return this.events.filter((e) =>
      e.time >= since && Math.hypot(e.x - x, e.z - z) <= radius + e.radius);
  }

  /** The retained log, oldest first. Bounded — see MAX_EVENTS. */
  all(): readonly EventRecord[] {
    return this.events;
  }

  /**
   * The log plus the id cursor. `seq` must survive the trip: ids mint from
   * emission order across the whole run, so a restored world keeps minting
   * exactly the ids live play would have.
   */
  toSnapshot(): EventBusSnapshot {
    return {
      seq: this.seq,
      records: this.events.map((e) => ({
        ...e,
        cause: { ...e.cause },
        ...(e.parentEventIds ? { parentEventIds: [...e.parentEventIds] } : {}),
        ...(e.data ? { data: { ...e.data } } : {}),
      })),
    };
  }

  /** Replace the log and cursor in place. Shares nothing with the source. */
  restore(snap: EventBusSnapshot): void {
    this.seq = snap.seq;
    this.events.length = 0;
    for (const e of snap.records) {
      this.events.push({
        ...e,
        cause: { ...e.cause },
        ...(e.parentEventIds ? { parentEventIds: [...e.parentEventIds] } : {}),
        ...(e.data ? { data: { ...e.data } } : {}),
      });
    }
  }
}
